import { each, upperFirst } from 'lodash';

import { IInvalidFieldErrors, ValidationErrorMessages } from './types';
import {ERROR_MESSAGE_DEFAULT_TEXT} from "../errorHandling/constants";

function cleanedErrorMessage(errorMsg) {
  if (errorMsg.substr(-1) !== '.') {
    return upperFirst(`${errorMsg}.`);
  }
  return upperFirst(errorMsg);
}

// Iterates over the error hash Rails returns when rejecting a model as invalid.
// We provide the option to replace field names with a different name if we don't want
// to use the default humanization from Rails
export function formErrorFullMessages(
  errors: IInvalidFieldErrors,
  fieldNameMap: StringMap = {},
): ValidationErrorMessages {
  const errorMessages: Array<string> = [];
  each(errors, (errorMessagesForField, fieldName) => {
    each(errorMessagesForField, (errorMessage) => {
      let combinedErrorMessage;
      if (fieldName === 'base') {
        // Base means that is is a general error message not applying to a specific field,
        // indicating that we can push in the raw message
        combinedErrorMessage = errorMessage;
      } else {
        let humanizedFieldName = fieldName;
        // Use a manual override of the field name if it exists in map
        if (fieldNameMap[fieldName]) {
          humanizedFieldName = fieldNameMap[fieldName];
        } else {
          humanizedFieldName = fieldName;
        }
        combinedErrorMessage = `${humanizedFieldName} ${errorMessage}`;
      }

      errorMessages.push(cleanedErrorMessage(combinedErrorMessage));
    });
  });
  return errorMessages;
}

interface StringMap {
  [key: string]: string;
}

// // TODO: Some way to make Concierge always be a link to the Chat page
// export const DEFAULT_ERROR_MESSAGE =
//   "Sorry, but we've experienced an unexpected error. The issue has been logged and will be resolved promptly. If you need assistance, you can always chat with your Flight Concierge.";
// <>Sorry, but we have experienced an unexpected error. The issue has been logged and will be resolved promptly. If you need assistance, you can always <a href="/concierge">chat</a> with your Flight Concierge.</>;

const parseFallbackErrors = (errorResponse): string[] => {
  if (errorResponse.validationErrorMessages?.length) {
    return errorResponse.validationErrorMessages;
  }

  if (errorResponse.errorMessages?.length) {
    return errorResponse.errorMessages;
  }

  if (errorResponse.errorMessage?.length) {
    return [errorResponse.errorMessage];
  }

  if (errorResponse.errors?.length) {
    return errorResponse.errors;
  }

  if (errorResponse.error?.length) {
    return [errorResponse.error];
  }

  return [ERROR_MESSAGE_DEFAULT_TEXT];
};

/**
 * parseErrorMessagesFromResponse
 *
 * @param errorResponse: the request response returned by Fetch
 *
 * Returns an array of human readable error messages as defined by the server:
 *
 * ["Email is too short", "Email is not valid", "Name is too short"]
 * This format would typically be used so that we could either humanize the field name in a client-specific fashion,
 * or if we wanted to know the errant fields so we could highlight the UX inputs in red
 *
 */
export const parseErrorMessagesFromResponse = (
  errorResponse,
  fieldNameMap: StringMap = {},
): ValidationErrorMessages => {
  if (errorResponse.status === 422) {
    if (fieldNameMap) {
      if (errorResponse.invalidFields?.length) {
        return formErrorFullMessages(errorResponse.invalidFields, fieldNameMap);
      }

      if (errorResponse.errors?.length) {
        // eslint-disable-next-line no-console
        console.error('[error refactor] parseErrorMessagesFromResponse encountered old errors attribute');
        // eslint-disable-next-line no-console
        console.error(errorResponse);
        return formErrorFullMessages(errorResponse.errors, fieldNameMap);
      }

      return parseFallbackErrors(errorResponse);
    }

    if (errorResponse.validationErrorMessages?.length) {
      return errorResponse.validationErrorMessages;
    }

    // eslint-disable-next-line no-console
    console.error('[error refactor] parseErrorMessagesFromResponse fell back to fallback errors in 422');
    // eslint-disable-next-line no-console
    console.error(errorResponse);
    return parseFallbackErrors(errorResponse);
  }

  if (errorResponse.status === 429) {
    return ["We're currently experiencing a high volume of searches. Please try again a bit later."];
  }

  // This may not be a real case
  if (errorResponse.status > 200 && errorResponse.status !== 500 && errorResponse.data?.errorMessage?.length) {
    // eslint-disable-next-line no-console
    console.error('[error refactor] parseErrorMessagesFromResponse encountered unexpected case 1');
    // eslint-disable-next-line no-console
    console.error(errorResponse);

    return [errorResponse.data.errorMessage];
  }

  if (errorResponse.status === 404 && errorResponse.errorMessages) {
    return errorResponse.errorMessages;
  }

  // This may not be a real case
  if (errorResponse?.data?.errorMessage) {
    // eslint-disable-next-line no-console
    console.error('[error refactor] parseErrorMessagesFromResponse encountered unexpected case 2');
    // eslint-disable-next-line no-console
    console.error(errorResponse);
    return [errorResponse?.data?.errorMessage];
  }

  return [ERROR_MESSAGE_DEFAULT_TEXT];
};
